import LINKS from "@constants/links";
import { PRODUCTS } from "@constants/prices";
import { usePurchaseFlow } from "@contexts/PurchaseFlowContext";
import { useToken } from "@contexts/TokenContext";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Divider,
  FormHelperText,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { useApplyCoupon } from "@mutations/useApplyCoupon";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { CartInfoDisplay } from "./CartInfoDisplay";

export default function Cart() {
  const { watch, setValue, control } = useFormContext();
  const { currentStep, stepComponents } = usePurchaseFlow();
  const { isAuthenticated } = useToken();
  const location = useLocation();
  const [promoError, setPromoError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Values still needed locally
  const items: { sku: string; quantity: number }[] = watch("items") || [];
  const promoCode = watch("couponCode");
  const redirectUrl = watch("redirectUrl");

  const { mutate, isPending } = useApplyCoupon();
  const handleApplyCoupon = () => {
    setPromoError(null); // Clear any previous errors
    mutate(
      {
        promoCode,
        cartProducts: items.map(({ sku }) => ({ sku })),
      },
      {
        onSuccess: (res) => {
          if (res?.valid) {
            setValue("couponAmountOff", res?.data?.amountOff);
            setValue("couponPercentOff", res?.data?.percentOff);
            setValue("couponId", res?.data?.id);
            setValue("couponData", res?.data);
            setPromoError(null);
            setSnackbarOpen(true);
          } else {
            setPromoError("Invalid promo code");
          }
        },
        onError: () => {
          setPromoError("Invalid promo code");
        },
      },
    );
  };

  const getDisclaimer = (): string => {
    const validProduct = items.find(({ sku }) => {
      const product = PRODUCTS[sku];
      return (
        product &&
        product.type === "subscription" &&
        !["promo", "digitalProduct"].includes(product.type) &&
        !sku.includes("YearToYear")
      );
    });

    if (!validProduct) return "";

    const productData = PRODUCTS[validProduct.sku];
    const productCategory = productData.productCategories?.[0] || "";

    return productCategory === "rrp"
      ? "*Subscription starts 30 days after purchase or upon training completion, whichever comes first."
      : "*Subscription starts 30 days after purchase or upon certification at training completion, whichever comes first.";
  };

  const disclaimer = getDisclaimer();

  // Hide promo code on purchase complete page
  const isPurchaseCompletePage = location.pathname.includes("/purchase-complete");

  // Validate redirect URL and provide fallback
  const getValidRedirectUrl = (): string => {
    if (!redirectUrl) return LINKS.newPurchase.redirectUrl;

    try {
      const url = new URL(redirectUrl);
      const allowedOrigins = new Set(["https://integratedlistening.com", "https://my.unyte.com"]);
      return allowedOrigins.has(url.origin) ? redirectUrl : LINKS.newPurchase.redirectUrl;
    } catch {
      return LINKS.newPurchase.redirectUrl;
    }
  };

  const handleChangeClick = () => {
    if (isAuthenticated) {
      // For authenticated users, send message to parent iframe to navigate back
      window.parent.postMessage({ action: "navigateBack" }, "*");
    } else {
      // For non-authenticated users, redirect to the product selection page
      const validUrl = getValidRedirectUrl();
      window.location.href = validUrl;
    }
  };

  const isOnConfirmStep = currentStep === stepComponents.length;

  return (
    <Card variant="outlined" sx={{ p: 1 }}>
      {/* Coupon code - only show on confirm step and not on purchase complete page */}
      {isOnConfirmStep && !isPurchaseCompletePage && (
        <>
          <Stack spacing={1} py={3} px={2}>
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
              <Controller
                name="couponCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Have a promo code?"
                    size="small"
                    disabled={isPending}
                    error={!!promoError}
                    onChange={(e) => {
                      field.onChange(e);
                      if (promoError) setPromoError(null); // Clear error when typing
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                size="large"
                loading={isPending}
                onClick={handleApplyCoupon}
                disabled={!promoCode}
              >
                Apply
              </Button>
            </Stack>
            {promoError && (
              <FormHelperText error sx={{ textAlign: "left" }}>
                {promoError}
              </FormHelperText>
            )}
          </Stack>
          <Divider />
        </>
      )}

      <CardHeader title="Your Purchase Selection" slotProps={{ title: { variant: "h5" } }} />

      {items.length > 0 && (
        <CartInfoDisplay
          disclaimer={disclaimer}
          isPurchaseCompletePage={isPurchaseCompletePage}
          handleChangeClick={handleChangeClick}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "primary.main",
          },
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          color="success"
          variant="filled"
          sx={{ width: "100%", color: "white" }}
        >
          Promo code applied
        </Alert>
      </Snackbar>
    </Card>
  );
}
